import { get, isEmpty, isNil } from "lodash";
import { SLOT_TYPES } from "./workshop-view";

const copyFormState = (live, copy) => {
  const liveFields = live.querySelectorAll("input, select, textarea");
  const copyFields = copy.querySelectorAll("input, select, textarea");
  liveFields.forEach((field, index) => {
    const target = copyFields[index];
    if (isNil(target)) return;
    if (field.tagName === "SELECT") {
      Array.from(target.options).forEach((option, optionIndex) => {
        if (optionIndex === field.selectedIndex)
          option.setAttribute("selected", "selected");
        else option.removeAttribute("selected");
      });
      return;
    }
    if (field.type === "checkbox" || field.type === "radio") {
      if (field.checked) target.setAttribute("checked", "checked");
      else target.removeAttribute("checked");
      return;
    }
    if (field.tagName === "TEXTAREA") {
      target.textContent = field.value;
      return;
    }
    target.setAttribute("value", field.value);
  });
};

export const snapshotHtml = (doc) => {
  if (isNil(doc) || isNil(doc.body)) return "";
  const copy = doc.body.cloneNode(true);
  copyFormState(doc.body, copy);
  for (const script of copy.querySelectorAll("script")) script.remove();
  return copy.innerHTML;
};

const SLOT_ATTRIBUTE = "data-kayros-slot";

const pathOf = (root, node) => {
  const path = [];
  for (let current = node; current !== root; current = current.parentElement)
    path.unshift(Array.from(current.parentElement.children).indexOf(current));
  return path;
};

const nodeAt = (root, path) =>
  path.reduce(
    (node, index) => (isNil(node) ? null : node.children[index] || null),
    root,
  );

const isCopyOf = (item, template) =>
  !isNil(item) &&
  item.tagName === template.tagName &&
  (item.hasAttribute("data-collection-item") ||
    item.getAttribute("class") === template.getAttribute("class"));

const copiesOf = (doc, template, list) => {
  const portal = doc.querySelector(
    `[data-collection-portal="${get(list, "binding.source")}"]`,
  );
  if (!isNil(portal))
    return Array.from(portal.children)
      .map((wrapper) => wrapper.firstElementChild)
      .filter((item) => isCopyOf(item, template));
  const own = doc.querySelector(list.templateSelector);
  if (isNil(own) || isNil(own.parentElement)) return [];
  return Array.from(own.parentElement.children).filter(
    (item) => item !== own && isCopyOf(item, template),
  );
};

export const mirrorListSlots = ({ html, sourceHtml, slots = [] }) => {
  const lists = slots.filter(
    (slot) => slot.type === SLOT_TYPES.LIST && !isEmpty(slot.templateSelector),
  );
  if (isEmpty(html) || isEmpty(lists)) return html;
  const parser = new DOMParser();
  const source = parser.parseFromString(sourceHtml || html, "text/html");
  const doc = parser.parseFromString(html, "text/html");
  let changed = false;
  for (const list of lists) {
    const template = source.querySelector(list.templateSelector);
    if (isNil(template)) continue;
    const marked = Array.from(template.querySelectorAll(`[${SLOT_ATTRIBUTE}]`));
    for (const copy of copiesOf(doc, template, list)) {
      for (const node of marked) {
        const target = nodeAt(copy, pathOf(template, node));
        if (
          isNil(target) ||
          target.tagName !== node.tagName ||
          target.hasAttribute(SLOT_ATTRIBUTE)
        )
          continue;
        target.setAttribute(SLOT_ATTRIBUTE, node.getAttribute(SLOT_ATTRIBUTE));
        changed = true;
      }
    }
  }
  if (!changed) return html;
  return html.indexOf("<html") >= 0
    ? `<!DOCTYPE html>${doc.documentElement.outerHTML}`
    : `${doc.head.innerHTML}${doc.body.innerHTML}`;
};

export const previewVersion = ({ id, htmlContent, styleContent, slots }) => ({
  id: id || "workshop-preview",
  htmlContent: htmlContent || "",
  styleContent: styleContent || "",
  states: [],
  bindingManifest: { slots: slots || [] },
});

const ISO_DATE = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}/;

export const answerText = (value) => {
  if (isNil(value)) return "";
  if (Array.isArray(value)) return value.join(", ");
  const match = ISO_DATE.exec(String(value));
  return match ? match[1] : String(value);
};

export const answersByStep = (answers = []) =>
  answers.reduce((groups, answer) => {
    const stepId = get(answer, "step.id") || "";
    const group = groups.find((item) => item.id === stepId);
    if (group) {
      group.answers.push(answer);
      return groups;
    }
    return [
      ...groups,
      { id: stepId, name: get(answer, "step.name") || "", answers: [answer] },
    ];
  }, []);
